
import React from 'react';
import { Bolt, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

interface PromptEditorProps {
  projectDescription: string;
  setProjectDescription: (value: string) => void;
  additionalDescription: string;
  setAdditionalDescription: (value: string) => void;
  isGenerating: boolean;
  onRegenerate: () => void;
  hasPrompts: boolean;
}

const PromptEditor = ({
  projectDescription,
  setProjectDescription,
  additionalDescription,
  setAdditionalDescription,
  isGenerating,
  onRegenerate,
  hasPrompts
}: PromptEditorProps) => {
  
  const appendToDescription = (textToAdd: string) => {
    const updatedText = additionalDescription ? `${additionalDescription} ${textToAdd}` : textToAdd;
    setAdditionalDescription(updatedText);
  };
  
  return (
    <Card className="bg-gray-800/30 backdrop-blur border-white/5">
      <CardContent className="p-6">
        <div className="flex mb-4 border-b border-white/10 pb-2">
          <h3 className="flex-1 py-2 text-center font-medium flex items-center justify-center gap-2 text-purple-400">
            <span className="bg-gradient-to-r from-indigo-500 to-purple-500 w-1.5 h-5 rounded mr-1"></span>
            <span>Edit Your Request</span>
          </h3>
        </div>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="projectDescription" className="block text-sm font-medium mb-2 flex items-center justify-between">
              <span>Project Description</span>
              <span className="text-xs text-muted-foreground">{projectDescription.length} chars</span>
            </label>
            <Textarea
              id="projectDescription"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              className="min-h-[120px] bg-gray-900/50 border-white/10 focus:border-primary font-medium"
            />
          </div>
          
          <div>
            <label htmlFor="additionalDescription" className="block text-sm font-medium mb-2 flex items-center justify-between">
              <span>Additional Details (optional)</span>
              <span className="text-xs text-muted-foreground">{additionalDescription.length} chars</span>
            </label>
            <Textarea
              id="additionalDescription"
              value={additionalDescription}
              onChange={(e) => setAdditionalDescription(e.target.value)}
              placeholder="Add specific technologies, features, or design preferences to enhance your prompts..."
              className="min-h-[100px] bg-gray-900/50 border-white/10 focus:border-primary"
            />
          </div>
          
          <Button 
            onClick={onRegenerate}
            disabled={isGenerating || !projectDescription.trim()}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 transition-opacity py-6 h-auto"
          >
            {isGenerating ? (
              <>
                <span className="mr-2">Generating</span>
                <div className="h-4 w-4 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
              </>
            ) : (
              <>
                <Bolt className="mr-2 h-4 w-4" />
                {hasPrompts ? 'Enhance Prompts' : 'Generate Prompts'}
              </>
            )}
          </Button>

          <div className="pt-2 border-t border-white/10">
            <div className="text-sm font-medium mb-3">Quick Templates</div>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gray-800/50 border-white/10 hover:bg-secondary justify-start"
                onClick={() => appendToDescription("Include user authentication with JWT.")}
              >
                <PlusCircle className="h-3 w-3 mr-1" />
                <span className="text-xs">Authentication</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gray-800/50 border-white/10 hover:bg-secondary justify-start"
                onClick={() => appendToDescription("Add dark/light theme toggle.")}
              >
                <PlusCircle className="h-3 w-3 mr-1" />
                <span className="text-xs">Dark Mode</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gray-800/50 border-white/10 hover:bg-secondary justify-start"
                onClick={() => appendToDescription("Implement responsive mobile design.")}
              >
                <PlusCircle className="h-3 w-3 mr-1" />
                <span className="text-xs">Mobile-First</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gray-800/50 border-white/10 hover:bg-secondary justify-start"
                onClick={() => appendToDescription("Add animations and transitions.")}
              >
                <PlusCircle className="h-3 w-3 mr-1" />
                <span className="text-xs">Animations</span>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PromptEditor;
